import { useEffect, useMemo, useState, useRef } from "react";
import { AppShell } from "./components/ui/AppShell";
import { Card } from "./components/ui/Card";
import { ResizableSplit } from "./components/ui/ResizableSplit";
import { SearchBox } from "./components/search/SearchBox";
import { TableView } from "./components/table/TableView";
import { DetailsPane } from "./components/features/DetailsPane";
import { TestPanel } from "./components/test/TestPanel";
import { UploadDialog } from "./components/features/UploadDialog";
import { UploadNotificationArea, type UploadNotification } from "./components/features/UploadNotification";
import { colors } from "./constants/theme";
import type { Item, SearchFilter } from "./types";
import { filterItems } from "./utils/search";
import { useRealtimeQueue } from "./hooks/useRealtimeQueue";
import { api } from "./utils/api";

export default function App() {
  const [tab, setTab] = useState(0); // 0=Queue, 1=Database
  const [queue, setQueue] = useState<Item[]>([]);
  const [db, setDb] = useState<Item[]>([]);
  const [loading, setLoading] = useState({ queue: true, db: true });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [loadingItem, setLoadingItem] = useState(false);
  const hasFetchedQueue = useRef(false);
  const hasFetchedDb = useRef(false);

  // Details-tab state (right pane)
  const [detailsTab, setDetailsTab] = useState<"meta" | "agents">("meta");

  // Search state
  const [searchText, setSearchText] = useState("");
  const [searchFilter, setSearchFilter] = useState<SearchFilter>({ query: "" });

  // Data selection
  const data = useMemo(() => (tab === 0 ? queue : db), [tab, queue, db]);
  const filtered = useMemo(() => filterItems(data, searchFilter.query ? searchFilter : { field: undefined, query: searchText }), [data, searchFilter, searchText]);
  // Use selectedItem if available, otherwise fall back to local data
  const selected = selectedItem || (selectedId ? data.find((x) => x.id === selectedId) ?? null : null);

  // Reset details tab on tab switch or selection change
  useEffect(() => { setDetailsTab("meta"); }, [tab, selectedId]);

  // Fetch latest item from server when selected
  const handleItemSelect = async (id: string) => {
    setSelectedId(id);
    setSelectedItem(null); // Clear previous selection while loading
    setLoadingItem(true);
    
    try {
      const endpoint = tab === 0 ? `/queue/${id}` : `/db/${id}`;
      const item = await api.get<Item>(endpoint);
      setSelectedItem(item);
      
      // Also update the item in local state
      if (tab === 0) {
        setQueue((prev) => prev.map((i) => (i.id === id ? item : i)));
      } else {
        setDb((prev) => prev.map((i) => (i.id === id ? item : i)));
      }
    } catch (error) {
      console.error('Failed to fetch item:', error);
      // Fall back to local data if fetch fails
      setSelectedItem(null);
    } finally {
      setLoadingItem(false);
    }
  };

  // Fetch initial data from API (only once)
  useEffect(() => {
    if (hasFetchedQueue.current) return;
    hasFetchedQueue.current = true;

    const fetchQueue = async () => {
      try {
        setLoading(prev => ({ ...prev, queue: true }));
        const data = await api.get<Item[]>('/queue');
        setQueue(data);
      } catch (error) {
        console.error('Failed to fetch queue:', error);
        setQueue([]);
      } finally {
        setLoading(prev => ({ ...prev, queue: false }));
      }
    };

    fetchQueue();
  }, []);

  useEffect(() => {
    if (hasFetchedDb.current) return;
    hasFetchedDb.current = true;

    const fetchDatabase = async () => {
      try {
        setLoading(prev => ({ ...prev, db: true }));
        const data = await api.get<Item[]>('/db');
        setDb(data);
      } catch (error) {
        console.error('Failed to fetch database:', error);
        setDb([]);
      } finally {
        setLoading(prev => ({ ...prev, db: false }));
      }
    };

    fetchDatabase();
  }, []);

  // WebSocket real-time updates (only for Queue tab)
  const { status: wsStatus, isConnected, hasMaxRetries, reconnect } = useRealtimeQueue({
    enabled: tab === 0,
    onItemAdded: (item) => {
      try {
        if (!item || !item.id) {
          console.warn('[App] Invalid item received in onItemAdded:', item);
          return;
        }
        setQueue((prev) => {
          // Don't add if item already exists (from initial fetch or previous add)
          if (prev.some(i => i.id === item.id)) {
            return prev;
          }
          return [item, ...prev].slice(0, 100);
        });
      } catch (error) {
        console.error('[App] Error handling item.added:', error, item);
      }
    },
    onItemUpdated: async (item) => {
      try {
        if (!item || !item.id) {
          console.warn('[App] Invalid item received in onItemUpdated:', item);
          return;
        }
        
        const itemId = Object.keys(item).length === 1 && 'id' in item ? item.id : item.id;
        
        // If lightweight update (just has id), refetch the item
        if (Object.keys(item).length === 1 && 'id' in item) {
          // Debounce refetch requests - only refetch if we haven't recently refetched this item
          setTimeout(async () => {
            try {
              const updated = await api.get<Item>(`/queue/${itemId}`);
              if (updated) {
                setQueue((prev) => prev.map((i) => (i.id === itemId ? updated : i)));
                
                // If this is the currently selected item, update selectedItem too
                if (selectedId === itemId && tab === 0) {
                  setSelectedItem(updated);
                }
              }
            } catch (error) {
              console.error('[App] Failed to refetch updated item:', error);
            }
          }, 100); // Small delay to batch rapid updates
        } else {
          // Full item update
          setQueue((prev) => {
            const existing = prev.find(i => i.id === item.id);
            if (!existing) return prev; // Don't add new items via update
            return prev.map((i) => (i.id === item.id ? item : i));
          });
          
          // If this is the currently selected item, update selectedItem too
          if (selectedId === item.id && tab === 0) {
            setSelectedItem(item);
          }
        }
      } catch (error) {
        console.error('[App] Error handling item.updated:', error, item);
      }
    },
    onItemStatusChange: (itemId, status) => {
      setQueue((prev) => prev.map((i) => {
        if (i.id !== itemId) return i;
        const updated = { ...i, status };
        // If this is the currently selected item, update selectedItem too
        if (selectedId === itemId && tab === 0) {
          setSelectedItem(updated);
        }
        return updated;
      }));
    },
    onActivityStarted: (itemId, activity) => {
      setQueue((prev) => prev.map((i) => {
        if (i.id !== itemId) return i;
        // Don't add duplicate activities
        if (i.activities?.some(a => a.id === activity.id)) {
          return i;
        }
        const updated = {
          ...i,
          status: "Processing",
          activities: [...(i.activities || []), activity],
        };
        // If this is the currently selected item, update selectedItem too
        if (selectedId === itemId && tab === 0) {
          setSelectedItem(updated);
        }
        return updated;
      }));
    },
    onActivityUpdated: (itemId, activity) => {
      setQueue((prev) => prev.map((i) => {
        if (i.id !== itemId) return i;
        const acts = (i.activities || []).slice();
        const activityIndex = acts.findIndex(a => a.id === activity.id);
        if (activityIndex >= 0) {
          acts[activityIndex] = activity;
        } else {
          // Activity not found, add it
          acts.push(activity);
        }
        const updated = { ...i, activities: acts };
        // If this is the currently selected item, update selectedItem too
        if (selectedId === itemId && tab === 0) {
          setSelectedItem(updated);
        }
        return updated;
      }));
    },
    onActivityCompleted: (itemId, activity) => {
      setQueue((prev) => prev.map((i) => {
        if (i.id !== itemId) return i;
        const acts = (i.activities || []).slice();
        const activityIndex = acts.findIndex(a => a.id === activity.id);
        if (activityIndex >= 0) {
          acts[activityIndex] = activity;
        } else {
          // Activity not found, add it
          acts.push(activity);
        }
        const updated = { ...i, activities: acts };
        // If this is the currently selected item, update selectedItem too
        if (selectedId === itemId && tab === 0) {
          setSelectedItem(updated);
        }
        return updated;
      }));
    },
  });


  const [isSaving, setIsSaving] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  // Upload state
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [uploadNotifications, setUploadNotifications] = useState<UploadNotification[]>([]);

  // Metadata editing (local only, not saved until Save button is clicked)
  const handleMetaChange = (field: keyof NonNullable<Item["meta"]>, v: string) => {
    if (!selected) return;
    
    const updatedMeta = { ...(selected.meta || {}), [field]: v };
    const update = (it: Item) => (it.id === selected.id ? { ...it, meta: updatedMeta } : it);
    
    // Local update only
    if (tab === 0) {
      const updatedItem = update(selected);
      setQueue((p) => p.map(update));
      // Also update selectedItem if it's the same item
      if (selectedItem && selectedItem.id === selected.id) {
        setSelectedItem(updatedItem);
      }
    } else {
      const updatedItem = update(selected);
      setDb((p) => p.map(update));
      // Also update selectedItem if it's the same item
      if (selectedItem && selectedItem.id === selected.id) {
        setSelectedItem(updatedItem);
      }
    }
  };

  // Save to server
  const handleSave = async () => {
    if (!selected) return;
    
    setIsSaving(true);
    try {
      const endpoint = tab === 0 ? `/queue/${selected.id}` : `/db/${selected.id}`;
      await api.put(endpoint, { meta: selected.meta });
      // Server will emit WebSocket event, which will trigger onItemUpdated to refresh the item
      // For database items, also refetch the item after save
      if (tab === 1) {
        try {
          const updated = await api.get<Item>(`/db/${selected.id}`);
          setDb((prev) => prev.map((i) => (i.id === selected.id ? updated : i)));
          // Update selectedItem if this is the selected item
          if (selectedId === selected.id) {
            setSelectedItem(updated);
          }
        } catch (error) {
          console.error('Failed to refetch updated database item:', error);
        }
      }
    } catch (error) {
      console.error('Failed to save metadata:', error);
      alert('Failed to save changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Update status
  const handleStatusChange = async (status: "Publish" | "Processing" | "Archived") => {
    if (!selected) return;
    
    setIsUpdatingStatus(true);
    try {
      // Map frontend status to backend status
      const backendStatus = status === "Publish" ? "completed" : status.toLowerCase();
      
      const endpoint = tab === 0 ? `/queue/${selected.id}` : `/db/${selected.id}`;
      await api.put(endpoint, { status: backendStatus });
      
      // Server will emit WebSocket event, which will trigger onItemUpdated to refresh the item
      // Also update local state immediately for better UX
      const updateItem = (it: Item) => (it.id === selected.id ? { ...it, status: backendStatus } : it);
      
      if (tab === 0) {
        setQueue((prev) => prev.map(updateItem));
      } else {
        setDb((prev) => prev.map(updateItem));
      }
      
      // Update selectedItem if this is the selected item
      if (selectedItem && selectedItem.id === selected.id) {
        setSelectedItem({ ...selectedItem, status: backendStatus });
      }
      
      // If status changed to completed or archived, item might move from queue to database
      // The WebSocket update will handle this, but we can also refetch to be sure
      if (backendStatus === "completed" || backendStatus === "archived") {
        // Item will be removed from queue and added to database via WebSocket
        // No need to manually move it here
      }
    } catch (error) {
      console.error('Failed to update status:', error);
      alert('Failed to update status. Please try again.');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // Upload handling
  const handleUpload = async (file: File) => {
    const notificationId = `upload-${Date.now()}-${Math.random()}`;
    const notification: UploadNotification = {
      id: notificationId,
      filename: file.name,
      status: "uploading",
    };

    // Add uploading notification
    setUploadNotifications((prev) => [...prev, notification]);

    try {
      await api.uploadFile(file);
      // Update to success
      setUploadNotifications((prev) =>
        prev.map((n) =>
          n.id === notificationId ? { ...n, status: "success" as const } : n
        )
      );
    } catch (error) {
      // Update to error
      setUploadNotifications((prev) =>
        prev.map((n) =>
          n.id === notificationId ? { ...n, status: "error" as const } : n
        )
      );
      throw error; // Re-throw so dialog can handle it
    }
  };

  const handleClearNotification = (id: string) => {
    setUploadNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  const handleClearSuccessful = () => {
    setUploadNotifications((prev) => prev.filter((n) => n.status !== "success"));
  };

  return (
    <>
      <AppShell 
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span>Image Processing Console (Light)</span>
            {tab === 0 && (
              <div style={{ 
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}>
                <span style={{ 
                  fontSize: 11, 
                  padding: "2px 6px", 
                  borderRadius: 4, 
                  background: isConnected ? "#e8f5e9" : "#fff8e1", 
                  color: isConnected ? "#2e7d32" : "#b58900",
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}>
                  <span style={{ 
                    width: 6, 
                    height: 6, 
                    borderRadius: "50%", 
                    background: isConnected ? "#2e7d32" : "#b58900",
                    display: "inline-block",
                  }} />
                  {isConnected ? "Live" : wsStatus === "connecting" ? "Connecting..." : "Offline"}
                </span>
                {hasMaxRetries && (
                  <button
                    onClick={reconnect}
                    style={{
                      fontSize: 11,
                      padding: "4px 8px",
                      borderRadius: 4,
                      border: `1px solid ${colors.accent}`,
                      background: colors.accent,
                      color: "#fff",
                      fontWeight: 600,
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Reconnect
                  </button>
                )}
              </div>
            )}
          </div>
        }
        tabs={["Queue", "Database"]} 
        tab={tab} 
        onTab={(v) => { setTab(v); setSelectedId(null); setSelectedItem(null); }}
        actions={
          <button
            onClick={() => setIsUploadDialogOpen(true)}
            style={{
              padding: "6px 12px",
              borderRadius: 8,
              border: `1px solid ${colors.accent}`,
              background: colors.accent,
              color: "#fff",
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            Upload
          </button>
        }
      >
      <div style={{ height: "100%", width: "100%", minHeight: 0 }}>
        <ResizableSplit
          defaultLeftWidth={400}
          minLeftWidth={300}
          maxLeftWidth={1200}
          gap={12}
        >
          {/* LEFT: List + Search */}
          <Card header={<>
            <b style={{ color: colors.text, fontSize: 14, fontWeight: 700 }}>{tab === 0 ? "Queue" : "Database"}</b>
            <div style={{ flex: 1 }} />
            <SearchBox value={searchText} onChange={(v) => { setSearchText(v); setSearchFilter({ query: "" }); }} onApply={(f) => { setSearchFilter({ field: f.field, query: f.query }); setSearchText(f.query); }} activeField={searchFilter.field} />
          </>}>
            {loading[tab === 0 ? 'queue' : 'db'] ? (
              <div style={{ padding: 20, textAlign: 'center', color: colors.text }}>Loading...</div>
            ) : (
              <TableView data={filtered} selectedId={selectedId} onSelect={handleItemSelect} isQueue={tab === 0} />
            )}
          </Card>

          {/* RIGHT: Preview + Details Tabs */}
          <Card header={<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <b style={{ color: colors.text }}>Details</b>
            <div style={{ flex: 1 }} />
            <div style={{ display: "flex", gap: 6 }}>
              <button onClick={() => setDetailsTab("meta")} style={{ padding: "6px 10px", borderRadius: 8, border: `1px solid ${detailsTab === "meta" ? colors.accent : colors.border}`, background: detailsTab === "meta" ? colors.accent : colors.card, color: detailsTab === "meta" ? "#fff" : colors.text, cursor: "pointer" }}>Metadata</button>
              <button onClick={() => setDetailsTab("agents")} style={{ padding: "6px 10px", borderRadius: 8, border: `1px solid ${detailsTab === "agents" ? colors.accent : colors.border}`, background: detailsTab === "agents" ? colors.accent : colors.card, color: detailsTab === "agents" ? "#fff" : colors.text, cursor: "pointer" }}>Agents</button>
            </div>
          </div>}>
            {loadingItem ? (
              <div style={{ padding: 20, textAlign: 'center', color: colors.text }}>Loading item...</div>
            ) : (
              <DetailsPane 
                item={selected} 
                detailsTab={detailsTab} 
                onDetailsTabChange={setDetailsTab} 
                onMetaChange={handleMetaChange}
                onSave={handleSave}
                onStatusChange={handleStatusChange}
                isSaving={isSaving}
                isUpdatingStatus={isUpdatingStatus}
              />
            )}
          </Card>
        </ResizableSplit>
      </div>

      {/* Tests */}
      <TestPanel />
      </AppShell>

      {/* Upload Dialog */}
      <UploadDialog
        isOpen={isUploadDialogOpen}
        onClose={() => setIsUploadDialogOpen(false)}
        onUpload={handleUpload}
      />

      {/* Upload Notifications */}
      <UploadNotificationArea
        notifications={uploadNotifications}
        onClear={handleClearNotification}
        onClearAll={handleClearSuccessful}
      />
    </>
  );
}

